import { memo, useCallback, useMemo, type ChangeEvent } from 'react';
import { Tooltip } from '@/components/Tooltip';
import { Dropdownlist } from '@/components/Dropdownlist';
import { getOfficerPluralLabel } from '@/utils/auth';
import { ATTACH_TO_OPTIONS } from '@/models/form-schema';
import type { UseSchemaDraftResult } from '../builder/useSchemaDraft';

export interface WysiwygTopToolbarProps {
  draft: UseSchemaDraftResult;
  forceShowAll: boolean;
  onForceShowAllChange: (next: boolean) => void;
  /** Disable controls (e.g. during save). */
  disabled?: boolean;
  /**
   * Persistent "Add section" affordance lives here so the canvas itself can
   * stay pristine — same visual layout as the read-only client form when
   * nothing is selected.
   */
  onAddSection: () => void;
}

/**
 * Sits above the WYSIWYG canvas. Holds schema-level controls (name, attachTo)
 * and the "Show all fields" toggle. Save/Cancel/Delete remain owned by
 * CustomFormsScreen so they share the reindex preview flow.
 *
 * Decision: the editor is desktop-only — these controls don't have a separate
 * mobile layout. Fits at ~960px+ alongside the form canvas.
 */
export const WysiwygTopToolbar = memo(function WysiwygTopToolbar({
  draft,
  forceShowAll,
  onForceShowAllChange,
  disabled = false,
  onAddSection,
}: WysiwygTopToolbarProps) {
  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.setName(e.target.value);
    },
    [draft],
  );
  const handleAttachToChange = useCallback(
    (val: string | number | null) => {
      draft.setAttachTo(typeof val === 'string' ? val : 'CLIENT');
    },
    [draft],
  );
  const handleShowAllChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onForceShowAllChange(e.target.checked);
    },
    [onForceShowAllChange],
  );
  const officersLabel = useMemo(() => getOfficerPluralLabel().toLowerCase(), []);

  return (
    <div className="wysiwyg-top-toolbar">
      <div className="wysiwyg-top-toolbar-cell wysiwyg-top-toolbar-cell--grow">
        <label className="wysiwyg-top-label" htmlFor="wysiwyg-form-name">
          Form name
        </label>
        <input
          id="wysiwyg-form-name"
          type="text"
          className="kstc-input"
          value={draft.draft.name}
          onChange={handleNameChange}
          disabled={disabled}
          placeholder="e.g. House Check Request"
        />
      </div>
      <div className="wysiwyg-top-toolbar-cell">
        <label className="wysiwyg-top-label">Used for</label>
        <Dropdownlist
          value={draft.draft.attachTo}
          items={ATTACH_TO_OPTIONS}
          sortItems={false}
          searchable={false}
          disabled={disabled}
          onChange={handleAttachToChange}
        />
      </div>
      <div className="wysiwyg-top-toolbar-cell wysiwyg-top-toolbar-cell--toggle">
        <label className="wysiwyg-top-toggle">
          <input
            type="checkbox"
            checked={forceShowAll}
            onChange={handleShowAllChange}
            disabled={disabled}
          />
          {' '}
          Show all fields while editing
          <Tooltip
            content={`Some fields only appear after an earlier field has a specific value (set under 'Show only when'). Turn this on to keep those fields visible so you can edit them. Turn off to see exactly what your ${officersLabel} will see as they go.`}
            position="bottom"
          />
        </label>
      </div>
      <div className="wysiwyg-top-toolbar-cell">
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary"
          onClick={onAddSection}
          disabled={disabled}
        >
          + Add section
        </button>
      </div>
    </div>
  );
});
